import { Link, useParams } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics, categories } from '@/mocks/comics';

export default function ComicPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="pt-24 md:pt-28 pb-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该漫画可能已下架或链接无效</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === comic.category);
  const relatedComics = comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 4);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: comic.title,
    author: { '@type': 'Person', name: comic.author },
    description: comic.description,
    datePublished: comic.updatedAt,
    inLanguage: 'zh-CN',
    genre: categoryInfo?.name || '',
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title={`${comic.title} - jm漫画在线阅读`}
        description={`${comic.title}：${comic.description.slice(0, 120)}... 作者${comic.author}，${comic.status}，评分${comic.rating}分，${comic.views}次阅读。`}
        keywords={`${comic.title},jm漫画,${comic.category}漫画,在线漫画阅读,${comic.author}`}
        canonicalUrl={`https://jmcomic.com/comic/${comic.id}`}
        ogType="book"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Navbar />

      <main className="pt-24 md:pt-28 pb-16 md:pb-20">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: `${categoryInfo?.name || '漫画'}`, to: `/category/${comic.category}` },
            { label: comic.title },
          ]}
        />

        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-6">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            <div className="flex-shrink-0 w-full sm:w-64 md:w-72 mx-auto lg:mx-0">
              <div className="rounded-xl overflow-hidden bg-background-100 aspect-[3/4]">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="mt-4 flex gap-3">
                <button className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer">
                  <i className="ri-book-open-line"></i>
                  开始阅读
                </button>
                <button className="w-10 h-10 rounded-full border border-background-200 flex items-center justify-center text-foreground-600 hover:text-primary-600 hover:border-primary-300 transition-all cursor-pointer">
                  <i className="ri-heart-line"></i>
                </button>
                <button className="w-10 h-10 rounded-full border border-background-200 flex items-center justify-center text-foreground-600 hover:text-primary-600 hover:border-primary-300 transition-all cursor-pointer">
                  <i className="ri-share-forward-line"></i>
                </button>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mt-3">
                <span className="inline-flex items-center gap-1 text-sm text-foreground-600">
                  <i className="ri-user-line text-xs"></i>
                  {comic.author}
                </span>
                <span className="text-foreground-300">|</span>
                <span className="inline-flex items-center gap-1 text-sm text-accent-600 font-semibold">
                  <i className="ri-star-fill text-xs"></i>
                  {comic.rating}分
                </span>
                <span className="text-foreground-300">|</span>
                <span className={`inline-flex items-center gap-1 text-sm font-medium ${comic.status === '连载中' ? 'text-accent-600' : 'text-secondary-600'}`}>
                  <i className={`text-xs ${comic.status === '连载中' ? 'ri-loader-4-line' : 'ri-check-double-line'}`}></i>
                  {comic.status}
                </span>
                <span className="text-foreground-300">|</span>
                <span className="inline-flex items-center gap-1 text-sm text-foreground-600">
                  <i className="ri-eye-line text-xs"></i>
                  {comic.views} 阅读
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-4">
                {comic.tags.map((tag) => (
                  <span key={tag} className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-6">
                <h2 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="mt-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-heading font-bold text-foreground-950">
                    章节列表
                  </h2>
                  <span className="text-xs text-foreground-500">共 {comic.chapters.length} 话</span>
                </div>

                <div className="border border-background-200/70 rounded-xl overflow-hidden">
                  {comic.chapters.map((ch, index) => (
                    <a
                      key={ch.id}
                      href="#"
                      className={`flex items-center justify-between px-5 py-3.5 hover:bg-background-100 transition-colors cursor-pointer ${
                        index < comic.chapters.length - 1 ? 'border-b border-background-200/60' : ''
                      }`}
                    >
                      <span className="text-sm text-foreground-800">{ch.title}</span>
                      <span className="text-xs text-foreground-400">{ch.updatedAt}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {relatedComics.length > 0 && (
            <div className="mt-16">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/comic/${rc.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4] bg-background-100">
                      <img
                        src={rc.cover}
                        alt={rc.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-primary-500 text-background-50">
                          {rc.rating}分
                        </span>
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {rc.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{rc.author}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}